import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SelectInputOption } from '@/components/form/SelectForm';
import { SelectInputField } from '@/components/form/SelectInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { handleError } from '@/services/gql/config/URQLProvider';
import {
  useCreateLocationMutation,
  useGetLocationsQuery,
  useGetOrganisationHierarchyQuery
} from '@/services/gql/graphql.generated';
import { useHolidayRegions, useTenantDefaultHolidayRegionLabel } from '@/services/holiday/useHolidayRegions';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../admin.translations';

interface AddLocationFormValues {
  name: string;
  division: string;
  holidayRegion: string;
}

interface AdminAddLocationModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminAddLocationModal: FunctionComponent<AdminAddLocationModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const [{ fetching }, createLocation] = useCreateLocationMutation();
  const methods = useForm<AddLocationFormValues>();

  const [{ data: hierarchyData, error: hierarchyError }] = useGetOrganisationHierarchyQuery();
  const [{ data: locationsData, error: locationsError }] = useGetLocationsQuery();

  if (hierarchyError) handleError(hierarchyError);
  if (locationsError) handleError(locationsError);

  const divisionOptions = useMemo<SelectInputOption[]>(() => {
    const rootDivision = hierarchyData?.getOrganisationHierarchy;
    if (!rootDivision) return [];

    const extractOptions = (division: any): SelectInputOption[] => {
      if (!division) return [];

      const currentOption = {
        id: division.id,
        name: division.name
      };

      const childOptions = (division.children || []).flatMap(extractOptions);

      return [currentOption, ...childOptions];
    };

    const mappedOptions = extractOptions(rootDivision);

    return [{ id: '', name: adminStrings.locationsSection.addDialog.divisionPlaceholder }, ...mappedOptions];
  }, [hierarchyData]);

  const holidayRegionOptions = useHolidayRegions();
  const tenantDefaultLabel = useTenantDefaultHolidayRegionLabel(
    adminStrings.locationsSection.addDialog.inheritOption,
    adminStrings.locationsSection.addDialog.inheritOption
  );
  const holidayRegionSelectOptions = useMemo<SelectInputOption[]>(
    () => [
      { id: '', name: tenantDefaultLabel },
      ...holidayRegionOptions.map(option => ({ id: option.value, name: option.name }))
    ],
    [holidayRegionOptions, tenantDefaultLabel]
  );

  const onSubmit = async (values: AddLocationFormValues) => {
    const selectedDivision = divisionOptions.find(option => option.name === values.division);
    const selectedHolidayRegion = holidayRegionSelectOptions.find(option => option.name === values.holidayRegion);

    const result = await createLocation({
      location: {
        name: values.name,
        holidayRegionCode: selectedHolidayRegion?.id || undefined,
        divisionId: selectedDivision?.id
      }
    });

    await handleMutationResult(result, {
      successMessage: strings.common.success,
      errorMessage: strings.common.error,
      customSuccessCheck: data => !!data?.createLocation,
      onSuccess: () => {
        methods.reset();
        onClose();
        const newLocationId = result.data?.createLocation?.id;
        if (newLocationId) {
          navigate(newLocationId);
        }
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} header={adminStrings.locationsSection.addDialog.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <SelectInputField
              name="division"
              id="division"
              label={adminStrings.locationsSection.addDialog.division}
              options={divisionOptions}
              rules={{ required: adminStrings.locationsSection.addDialog.divisionRequired }}
            />

            <TextField
              label={adminStrings.locationsSection.addDialog.newLocation}
              name="name"
              id="name"
              placeholder={adminStrings.locationsSection.addDialog.locationName}
              customValidatorMessage={methods.formState.errors.name?.message}
              rules={{
                validate: (value: string) => {
                  if (!value) return strings.common.required;
                  const isDuplicate = locationsData?.locations?.some(location => location?.name === value);
                  if (isDuplicate) return adminStrings.locationsSection.addDialog.uniqueName;
                  return true;
                }
              }}
            />

            <SelectInputField
              name="holidayRegion"
              id="holidayRegion"
              label={adminStrings.locationsSection.addDialog.holidayRegion}
              options={holidayRegionSelectOptions}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default AdminAddLocationModal;
